import {PopoverProps} from '@mui/material';
import {useCallback, useState} from 'react';
import {EntityUserCarePlansRepository} from '../../../../../../../../repositories/entity-user-care-plans';
import {processWithConcurrency} from '../../../../../../../../utils/functions/process-with-concurrency';
import {PlannerItem, PlannerItemDeleteData, PlannerItemRegisterData} from '../planner-item';

export function usePlannerItemMenu() {
  const [anchorEl, setAnchorEl] = useState<PopoverProps['anchorEl'] | undefined>();
  const [selectedItem, setSelectedItem] = useState<PlannerItem | undefined>();
  const [dataToDelete, setDataToDelete] = useState<PlannerItemDeleteData[]>([]);
  const [dataToRegisterItem, setDataToRegisterItem] = useState<PlannerItemRegisterData>();
  const open = Boolean(anchorEl);

  const selectItem = useCallback((event: React.MouseEvent<HTMLElement>, item: PlannerItem) => {
    // Make the menu appear near the mouse.
    const domRect = (event.target as HTMLElement).getBoundingClientRect();
    domRect.x = event.clientX; // adjust x to mouse position.
    setAnchorEl({getBoundingClientRect: () => domRect, nodeType: 1});
    setSelectedItem(item);
  }, []);

  const clearSelectedItem = useCallback(() => {
    setSelectedItem(undefined);
    setAnchorEl(undefined);
  }, []);

  const prepareToDelete = useCallback(
    (data: PlannerItemDeleteData[]) => {
      setDataToDelete(data);
      clearSelectedItem();
    },
    [clearSelectedItem]
  );

  const clearDelete = useCallback(() => {
    setDataToDelete([]);
  }, []);

  const deleteData = useCallback(async () => {
    try {
      await processWithConcurrency({
        data: dataToDelete,
        action: deleteDataByItemType,
        limit: 6,
      });
    } finally {
      clearDelete();
    }
  }, [dataToDelete, clearDelete]);

  const prepareToRegister = useCallback(
    (data: PlannerItemRegisterData) => {
      setDataToRegisterItem(data);
      clearSelectedItem();
    },
    [clearSelectedItem]
  );

  const clearRegisterData = useCallback(() => {
    setDataToRegisterItem(undefined);
  }, []);

  return {
    // --- Selected item to present menu dropdown ---
    anchorEl,
    open,
    selectedItem,
    selectItem,
    clearSelectedItem,
    // --- Delete data ---
    dataToDelete,
    deleteData,
    clearDelete,
    prepareToDelete,
    // --- register data ---
    dataToRegisterItem,
    prepareToRegister,
    clearRegisterData,
  };
}

async function deleteDataByItemType(data: PlannerItemDeleteData) {
  // ------- ACTIVITY -------
  if (data.item.type === 'activity') {
    const entityId = data.item.entityId;
    const userId = data.user.id;
    const activitiesSectorId = data.item.activitiesSector.id;
    const activitiesGroupEntryId = data.user.careTask.activitiesGroupEntryId;
    const activityTypeEntryId = data.user.careTask.activityTypeEntryId;

    if (!activitiesGroupEntryId) {
      throw new Error('activitiesGroupEntryId is required');
    }

    // Remove the activity type from the user care plan.
    return EntityUserCarePlansRepository.instance.removeActivityTypeOnCarePlan(
      entityId,
      userId,
      activitiesSectorId,
      activitiesGroupEntryId,
      activityTypeEntryId
    );
  }
  // ------- ANNIVERSARY -------
  else if (data.item.type === 'anniversary') {
    throw new Error(`No implementation: '${data.item.type}'`);
  }

  // @ts-ignore
  throw new Error(`No implementation: '${data.item.type}'`);
}
